import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Footer from "@/components/landing/Footer";

const EFFECTIVE_DATE = "December 1, 2025";

export const PrivacyPolicy = () => {
  return (
    <div data-testid="privacy-page" className="min-h-screen bg-white text-[#0A0A0A]">
      <header className="border-b border-[#E5E5E5] bg-white/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid="privacy-back-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#0A0A0A] hover:text-[#0052FF] transition-colors"
          >
            <ArrowLeft size={16} />
            Back to OperatorAI
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-[#0A0A0A] text-white font-extrabold text-sm tracking-tight">
              O
            </span>
            <span className="font-extrabold tracking-tight text-lg">
              OperatorAI
            </span>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 sm:px-8 py-16 sm:py-24">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#0052FF] mb-5">
          Legal
        </div>
        <h1
          data-testid="privacy-headline"
          className="text-4xl sm:text-5xl font-extrabold tracking-tighter leading-tight text-[#0A0A0A]"
        >
          Privacy Policy
        </h1>
        <p className="mt-4 text-sm text-[#525252]">
          Effective Date: {EFFECTIVE_DATE}
        </p>

        <div className="prose mt-12 max-w-none text-[#0A0A0A]">
          <Section title="1. Who We Are">
            <p>
              OperatorAI (&ldquo;OperatorAI,&rdquo; &ldquo;we,&rdquo;
              &ldquo;us,&rdquo; or &ldquo;our&rdquo;) operates the website at{" "}
              <a className="text-[#0052FF] hover:underline" href="https://operatoraihq.com">
                operatoraihq.com
              </a>{" "}
              and provides educational content, newsletters, and resources
              about practical AI workflows for operators, consultants,
              agencies, and small businesses.
            </p>
            <p>
              This Privacy Policy explains what information we collect when
              you visit our site or subscribe to our content, how we use it,
              and the choices you have.
            </p>
          </Section>

          <Section title="2. Information We Collect">
            <p>We collect the following limited information:</p>
            <ul>
              <li>
                <strong>Email address</strong> &mdash; when you subscribe to
                the Free AI Workflow Guide or our weekly newsletter.
              </li>
              <li>
                <strong>Contact form submissions</strong> &mdash; your name,
                email address, and the contents of your message when you
                contact us.
              </li>
              <li>
                <strong>Basic usage analytics</strong> &mdash; standard
                information such as pages viewed, referring URL, device
                type, and approximate location, collected via privacy-
                respecting analytics tools.
              </li>
            </ul>
            <p>
              We do not knowingly collect personal data from children under
              the age of 13.
            </p>
          </Section>

          <Section title="3. How We Use Your Information">
            <p>We use the information we collect to:</p>
            <ul>
              <li>Deliver the Free AI Workflow Guide and weekly newsletter.</li>
              <li>Respond to your questions, partnerships, and inquiries.</li>
              <li>
                Improve the content, performance, and user experience of our
                site.
              </li>
              <li>
                Send occasional updates about new resources, tools, or
                offerings &mdash; you can unsubscribe at any time.
              </li>
            </ul>
          </Section>

          <Section title="4. How We Share Information">
            <p>
              We do <strong>not</strong> sell your personal information. We
              may share limited information only with:
            </p>
            <ul>
              <li>
                <strong>Service providers</strong> who help us operate the
                site, such as email delivery, hosting, and analytics
                providers, all bound by confidentiality obligations.
              </li>
              <li>
                <strong>Legal requirements</strong> &mdash; when required by
                applicable law, regulation, or valid legal process.
              </li>
            </ul>
          </Section>

          <Section title="5. Cookies & Tracking">
            <p>
              Our site may use cookies and similar technologies to remember
              preferences, measure traffic, and improve performance. You can
              control cookies through your browser settings.
            </p>
          </Section>

          <Section title="6. Affiliate Links">
            <p>
              Some content on this site may include affiliate links. If you
              click an affiliate link and make a purchase, we may earn a
              commission at no additional cost to you. We only recommend
              tools we believe provide real value to operators and small
              businesses.
            </p>
          </Section>

          <Section title="7. Data Retention">
            <p>
              We retain subscriber email addresses for as long as you are
              subscribed to our content. You can request deletion of your
              data at any time by emailing{" "}
              <a className="text-[#0052FF] hover:underline" href="mailto:hello@operatoraihq.com">
                hello@operatoraihq.com
              </a>
              .
            </p>
          </Section>

          <Section title="8. Your Rights">
            <p>
              Depending on where you live, you may have rights under
              applicable privacy laws (such as GDPR or CCPA), including the
              right to access, correct, or delete your personal information,
              or to opt out of certain processing. To exercise these rights,
              contact us at{" "}
              <a className="text-[#0052FF] hover:underline" href="mailto:hello@operatoraihq.com">
                hello@operatoraihq.com
              </a>
              .
            </p>
          </Section>

          <Section title="9. Security">
            <p>
              We use reasonable technical and organizational safeguards to
              protect your information. However, no method of transmission
              or storage is 100% secure.
            </p>
          </Section>

          <Section title="10. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. The
              effective date at the top of this page reflects the most
              recent revision. Material changes will be highlighted on this
              page.
            </p>
          </Section>

          <Section title="11. Contact Us">
            <p>
              Questions about this Privacy Policy? Email us at{" "}
              <a className="text-[#0052FF] hover:underline" href="mailto:hello@operatoraihq.com">
                hello@operatoraihq.com
              </a>
              .
            </p>
          </Section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

const Section = ({ title, children }) => (
  <section className="mt-10 first:mt-0">
    <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-[#0A0A0A]">
      {title}
    </h2>
    <div className="mt-4 space-y-4 text-base leading-relaxed text-[#374151]">
      {children}
    </div>
  </section>
);

export default PrivacyPolicy;
